import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import API from "../../util/api";
import PuzzleCaptcha from "../Common/PuzzleCaptcha";
import { fetchFreshPixelToken } from "../../util/pixelToken";

const OuterContainer = styled.div`
  width: 100%;
  margin: 0 auto;
  max-width: 800px;
  box-sizing: border-box;
`;

const TitleHeader = styled.div`
  width: 100%;
  background-color: #195174;
  padding: 2px 10px;
  border-radius: 10px 10px 0 0;
  box-sizing: border-box;
`;

const ForgetFormContainer = styled.div`
  background: rgba(255, 255, 255);
  padding: 20px;
  border-radius: 0px 0px 10px 10px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  text-align: left;
  width: 100%;
  box-sizing: border-box;
`;

const Title = styled.h3`
  color: #ffffff;
  font-size: 16px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  margin-bottom: 10px;
  font-size: 16px;
  border: 1px solid #195174;
  border-radius: 5px;
  box-sizing: border-box;
`;

const Button = styled.button`
  display: block;
  width: 100%;
  padding: 10px;
  margin-top: 10px;
  font-size: 16px;
  border: none;
  border-radius: 5px;
  background-color: #195174;
  color: white;
  cursor: pointer;
  font-weight: bold;

  &:hover {
    background-color: #2980b9;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
`;
const ErrorMessage = styled.div`
  background-color: ${(props) => props.status || "#f8d7da"};
  padding: 10px;
  margin-bottom: 10px;
`;

const SuccessMessage = styled.div`
  background-color: #d4edda;
  padding: 10px;
  margin-bottom: 10px;
`;

const CaptchaContainer = styled.div`
  background: #f0f0f0;
  padding: 20px;
  border-radius: 5px;
  margin-top: 20px;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
`;

const Paragraph = styled.p`
  margin-bottom: 1em;
`;

const HoneypotField = styled.div`
  position: absolute;
  left: -99999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

const ForgetPassword = () => {
  const [email, setEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [message, setMessage] = useState("");
  const [apiResponse,setAPIResponse] = useState("#d4edda");
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [timer, setTimer] = useState(0);
  const navigate = useNavigate();
  const [honeypotValue, setHoneypotValue] = useState("");

  useEffect(() => {
    const endTime = localStorage.getItem("otpResendEndTime");
    if (endTime) {
      const remainingTime = Math.floor((new Date(endTime) - new Date()) / 1000);
      if (remainingTime > 0) {
        setTimer(remainingTime);
        const interval = setInterval(() => {
          setTimer((prev) => {
            if (prev <= 1) {
              clearInterval(interval);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        localStorage.removeItem("otpResendEndTime");
      }
    }
  }, []);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  const startTimer = () => {
    const endTime = new Date(new Date().getTime() + 30000);
    localStorage.setItem("otpResendEndTime", endTime);
    setTimer(30);
  };

  const handleSendOTP = async () => {
    try {
      let pixelToken;
      try {
        pixelToken = await fetchFreshPixelToken();
      } catch (error) {
        console.warn('Unable to obtain pixel token for OTP request.', error);
        setMessage('Unable to initiate verification. Please refresh and try again.');
        return;
      }

      if (!pixelToken) {
        setMessage('Unable to initiate verification. Please refresh and try again.');
        return;
      }

      const response = await API.loginOTP(email, pixelToken, honeypotValue);
      startTimer();
      if (response.status === "Success") {
        setAPIResponse("#d4edda");
        setMessage("OTP sent to your email.");
        setOtpSent(true);
      } else {
        setAPIResponse("#f8d7da");
        setMessage(response.message);
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      setMessage("Failed to send OTP. Please try again.");
    }
  };

  const handleVerifyOTP = async () => {
    try {
      let pixelToken;
      try {
        pixelToken = await fetchFreshPixelToken();
      } catch (error) {
        console.warn("Unable to obtain pixel token for OTP verification.", error);
        setMessage("Unable to verify at this time. Please refresh and try again.");
        return;
      }

      if (!pixelToken) {
        setMessage("Unable to verify at this time. Please refresh and try again.");
        return;
      }

      // One call: the backend verifies the OTP and sends the reset link. Only
      // show the success screen if it confirms the email actually went out.
      const response = await API.forgetPassword(
        email,
        otpCode,
        pixelToken,
        honeypotValue,
      );
      if (response?.status === "Success") {
        setOtpVerified(true);
        setAPIResponse("#d4edda;");
        setMessage("An email has been sent with the reset password link.");
      } else {
        setAPIResponse("#f8d7da");
        setMessage(
          response?.message ||
            "We could not send the reset email. Please try again.",
        );
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setAPIResponse("#f8d7da");
      setMessage("We could not send the reset email. Please try again.");
    }
  };

  return (
    <OuterContainer>
      <TitleHeader>
        <Title>Forget Password</Title>
      </TitleHeader>
      <ForgetFormContainer>
        {!otpVerified ? (
          <>
            <Paragraph>
              To reset your password, please enter your email in the textbox
              below.
            </Paragraph>
            <Paragraph>
              Please use your email address to verify your identity.
            </Paragraph>
            {otpSent ? (
              <>
                <Input
                  type="text"
                  placeholder="Enter OTP"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                />
                {message && <ErrorMessage status={apiResponse}>{message}</ErrorMessage>}
                <Button onClick={handleSendOTP} disabled={timer > 0}>
                  {timer > 0 ? `Resend OTP (${timer}s)` : "Resend OTP"}
                </Button>
                <ButtonRow>
                  <Button onClick={() => setOtpSent(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleVerifyOTP}>Verify OTP</Button>
                </ButtonRow>
              </>
            ) : (
              <>
                <HoneypotField aria-hidden="true">
                  <label htmlFor="fp-website_url">Leave blank</label>
                  <input
                    id="fp-website_url"
                    name="website_url"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypotValue}
                    onChange={(event) => setHoneypotValue(event.target.value)}
                  />
                </HoneypotField>
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <CaptchaContainer>
                  <Paragraph>
                    Please solve the puzzle below to verify that you're a human.
                  </Paragraph>
                  <PuzzleCaptcha onVerified={setCaptchaVerified} />
                </CaptchaContainer>
                {message && <ErrorMessage>{message}</ErrorMessage>}
                <ButtonRow>
                  <Button onClick={() => navigate("/user/login")}>
                    Cancel
                  </Button>
                  <Button onClick={handleSendOTP} disabled={!captchaVerified || timer > 0}>
                    {timer > 0 ? `Resend OTP (${timer}s)` : "Send OTP"}
                  </Button>
                </ButtonRow>
              </>
            )}
          </>
        ) : (
          <>
            <SuccessMessage>
              An email has been sent with your password reset link. Please check your inbox. If you don’t see it within a few minutes, be sure to check your spam or junk folder.
            </SuccessMessage>
            <Button onClick={() => navigate("/user/login")}>Go to Login</Button>
          </>
        )}
      </ForgetFormContainer>
    </OuterContainer>
  );
};

export default ForgetPassword;
